import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getOrderStatusColor, ORDER_STATUS_LIST } from './common';
import SelectBox from '@common/src/common/selectbox/selectbox';
import EmptyData from '@common/src/common/Table/emptyData';
import InputBox from '@common/src/common/inputbox/inputbox';
import SearchBox from '@common/src/common/inputbox/searchBox';
import { ORDER_STATUS_OPTIONS, DISPUTE_ORDER_STATUS_OPTIONS, ALL_OPTION } from '@pulse/constants/constants';
import Pagination from '@pulse/components/common/Pagination/pagination';
import TextBreaker from '@common/src/common/Table/textBreaker';
import { formatDateTime, hasPermission, valuePlusFormatter } from '@common/src/common/helper';
import { INNER_PERMISSION } from '@common/src/constants/constants';
import { useSelector } from 'react-redux';

const OrderTable = ({
    orders,
    timeStamp = false,
    onFilter,
    searchQuery, searchData,
    selectedFilterStatus,
    filterStartDate,
    filterEndDate,
    disputeOrder = false,
    pagination,
    setPagination,
    type = 'offline',
    loading = false
}) => {
    const [selectedStatus, setSelectedStatus] = useState(selectedFilterStatus || []);
    const [selectedStartDate, setSelectedStartDate] = useState(filterStartDate || '');
    const [selectedEndDate, setSelectedEndDate] = useState(filterEndDate || '');
    const [searchItem, setSearchItem] = useState(searchData || '');
    const [statusOptions, setStatusOptions] = useState(ORDER_STATUS_OPTIONS);
    const { permissions } = useSelector((state) => state.auth)

    useEffect(() => {
        if (disputeOrder) {
            setStatusOptions(DISPUTE_ORDER_STATUS_OPTIONS);
        }
    }, [disputeOrder]);

    useEffect(() => {
        setSelectedStatus(selectedFilterStatus || []);
    }, [selectedFilterStatus]);

    // Function to clear all filters
    const clearFilters = () => {
        setSelectedStartDate('');
        setSelectedEndDate('');
        setSelectedStatus(ALL_OPTION.status);
        onFilter('', '', '', []); // Notify parent about cleared filters
    };
    return (
        <>
            <div className="box-body w-full flex flex-col lg:flex-row items-stretch lg:items-center gap-3 mb-4">

                {/* 🔍 LEFT: Search */}
                {!timeStamp && (
                    <div className="w-full lg:w-[30%]">
                        <div className="flex w-full">
                            <SearchBox
                                classes="py-2 px-3 w-full !ti-form-input !rounded-none !rounded-s-sm"
                                width="w-full"
                                placeHolder="Search by order number/phone number"
                                value={searchItem}
                                onChange={(e) => {
                                    setSearchItem(e.target.value);
                                    if (!e.target.value.trim()) searchQuery('');
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') searchQuery(searchItem.trim());
                                }}
                            />
                            <button
                                className="px-3 flex items-center justify-center bg-primary text-white rounded-tr-md rounded-br-md"
                                onClick={() => searchQuery(searchItem.trim())}
                            >
                                <i className="ri-search-line"></i>
                            </button>
                        </div>
                    </div>
                )}

                {/* 🎛 RIGHT: Filters + Buttons */}
                <div className="flex flex-wrap gap-2 w-full lg:w-auto lg:ml-auto">

                    <SelectBox
                        options={statusOptions}
                        placeholder="Select Status"
                        name="Status"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e)}
                        className="w-full sm:w-[180px]"
                    />

                    {!timeStamp && (
                        <>
                            <InputBox
                                type="date"
                                name="startDate"
                                value={selectedStartDate}
                                onChange={(e) => setSelectedStartDate(e.target.value)}
                                className="w-full sm:w-[180px]"
                            />

                            <InputBox
                                type="date"
                                name="endDate"
                                value={selectedEndDate}
                                onChange={(e) => setSelectedEndDate(e.target.value)}
                                className="w-full sm:w-[180px]"
                            />
                        </>
                    )}

                    <button
                        className="ti-btn !py-2 !px-3 text-xs text-white bg-primary w-full sm:w-auto"
                        onClick={() => onFilter(selectedStartDate, selectedEndDate, selectedStatus)}
                    >
                        Apply Filter
                    </button>

                    <button
                        className="ti-btn !py-2 !px-3 text-xs text-white bg-danger w-full sm:w-auto"
                        onClick={clearFilters}
                    >
                        Clear
                    </button>

                </div>
            </div>
            {!orders?.length && !loading ?
                <EmptyData icon="shopping-cart-line" text="No orders found" />
                :

                <div className="mt-6 table-responsive">
                    <table className="table whitespace-nowrap min-w-full">
                        <thead>
                            <tr className="border-b border-defaultborder">
                                <th scope="col" className="text-start">Sl.No</th>
                                <th scope="col" className="text-start">Order No</th>
                                <th scope="col" className="text-start">Order By</th>
                                <th scope="col" className="text-start">Phone</th>
                                <th scope="col" className="text-start">Outlet Code</th>
                                <th scope="col" className="text-start">Store Location</th>
                                <th scope="col" className="text-start">Order Value</th>
                                <th scope="col" className="text-start">Payment Type</th>
                                <th scope="col" className="text-start">Order Date & Time</th>
                                <th scope="col" className="text-start">Status</th>
                                <th scope="col" className="text-start">Service</th>
                                {hasPermission(permissions, INNER_PERMISSION.ORDER_STATUS_DETAILS) && (
                                    <th scope="col" className="text-start">View</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((item) => {
                                let timeZone = item?.meta?.config?.country?.timezone;
                                return (
                                    <tr key={item.id} className={`border-b border-defaultborder`}>
                                        <td scope="row">
                                            {pagination ? ((pagination?.currentPage - 1) * pagination?.limit + orders.indexOf(item) + 1) : orders.indexOf(item) + 1}</td>
                                        <td scope="row">
                                            <TextBreaker text={item?.orderNumber} />
                                        </td>
                                        <td>
                                            <TextBreaker
                                                text={
                                                    item?.customer?.name
                                                    ||
                                                    (item?.customer?.phoneNumber === '1' 
                                                        ? item?.customer?.firstName 
                                                        : `${item?.customer?.firstName} ${item?.customer?.lastName}`)
                                                }
                                            />
                                        </td>

                                        <td>{item?.address?.phone || (item?.customer?.phoneNumber === '1' ? '' : item?.customer?.phoneNumber) || ''}</td>
                                        <td>
                                            <TextBreaker text={item?.store?.outletCode || ''} />
                                        </td>
                                        <td>
                                            <TextBreaker text={
                                                item?.store?.address?.city?.en
                                                || item?.store?.address?.city?.ar
                                                || item?.store?.name?.en
                                                || item?.store?.name?.ar
                                                || ''} />
                                        </td>
                                        <td>{item?.meta?.config?.currency?.currencyCode} {Number(item?.total).toFixed(2)}</td>
                                        <td>{valuePlusFormatter(item?.paymentMode)}</td>
                                        <td>
                                            {formatDateTime(item.orderDate, timeZone, timeStamp)}
                                        </td>
                                        <td>
                                            <span className={`badge ${getOrderStatusColor(item.orderStatus)}`} >
                                                {ORDER_STATUS_LIST[item.orderStatus]}
                                            </span>

                                            {disputeOrder && (
                                                <>
                                                    <br />
                                                    <TextBreaker text={item?.dispute || ''} />
                                                </>
                                            )}
                                        </td>
                                        <td>{item?.service?.replace(/_/g, ' ')}</td>
                                        <td>
                                            <Link
                                                aria-label="anchor"
                                                to={`/order-details?orderId=${item.orderId}`}
                                                className="ti-btn ti-btn-wave !gap-0 !m-0 !h-[1.75rem] !w-[1.75rem] text-[0.8rem] bg-info/10 text-info hover:bg-info hover:text-white hover:border-info"
                                            >
                                                <i className="ri-eye-line"></i>
                                            </Link>

                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>

                    {pagination && (
                        <Pagination pagination={pagination} setPagination={setPagination} rows={orders} />
                    )}
                </div>
            }
        </>
    )
}

OrderTable.propTypes = {
    orders: PropTypes.array,
    timeStamp: PropTypes.bool,
    onFilter: PropTypes.func,
    searchQuery: PropTypes.func,
    searchData: PropTypes.string,
    selectedFilterStatus: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
    filterStartDate: PropTypes.string,
    filterEndDate: PropTypes.string,
    disputeOrder: PropTypes.bool,
    pagination: PropTypes.object.isRequired,
    setPagination: PropTypes.func.isRequired,
    type: PropTypes.string, // 'offline' or 'live'
    loading: PropTypes.bool,
};

export default OrderTable;
